import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPublicChecklist } from '@/api/checklist';
import { useBranding } from '@/hooks/useBranding';

export function ChecklistPublic() {
  const { name: brand } = useBranding();
  const { data: items = [], isLoading, error } = useQuery({
    queryKey: ['checklist-public'],
    queryFn: getPublicChecklist,
  });

  // Tick state lives in component memory — never persisted. Reloading the
  // page wipes it, which matches "пройти чек-лист перед публикацией".
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  // Drop ticks for items that have been removed since the page loaded.
  useEffect(() => {
    setChecked((prev) => {
      const next: Record<string, boolean> = {};
      for (const it of items) if (prev[it.id]) next[it.id] = true;
      return next;
    });
  }, [items]);

  const done = useMemo(
    () => items.filter((it) => checked[it.id]).length,
    [items, checked]
  );
  const total = items.length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);

  return (
    <div className="min-h-screen bg-surface-sunken py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-content">
            <span className="text-primary">{brand}</span> Pre-publish Checklist
          </h1>
          <p className="mt-1 text-sm text-content-muted">
            Tick each item once it's done. Nothing is saved — reloading the page clears your marks.
          </p>
        </div>

        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="text-sm font-medium text-content-muted">
              {done} / {total} done
            </div>
            <div className="w-40 h-2 bg-surface-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-200"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {isLoading && (
            <div className="px-4 py-6 text-sm text-content-subtle">Loading…</div>
          )}
          {error && (
            <div className="px-4 py-6 text-sm text-danger">
              Failed to load checklist.
            </div>
          )}
          {!isLoading && !error && total === 0 && (
            <div className="px-4 py-6 text-sm text-content-subtle">
              No checklist items yet.
            </div>
          )}

          <ul className="divide-y divide-border">
            {items.map((it) => {
              const isChecked = !!checked[it.id];
              return (
                <li key={it.id}>
                  <label className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-surface-muted">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) =>
                        setChecked((prev) => ({ ...prev, [it.id]: e.target.checked }))
                      }
                      className="mt-1 w-5 h-5 rounded border-border-strong text-primary focus:ring-primary"
                    />
                    <span className="flex-1 min-w-0">
                      <span
                        className={`block text-sm leading-6 ${
                          isChecked ? 'line-through text-content-subtle' : 'text-content'
                        }`}
                      >
                        {it.text}
                      </span>
                      {it.description && (
                        <span
                          className={`block mt-0.5 text-xs leading-5 whitespace-pre-wrap break-words ${
                            isChecked ? 'text-content-subtle' : 'text-content-subtle'
                          }`}
                        >
                          {it.description}
                        </span>
                      )}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mt-4 text-center">
          <a
            href="/checklist/webview-test"
            target="_blank"
            rel="noreferrer"
            className="inline-block text-sm text-primary hover:underline"
          >
            → Open Webview Test page (camera, photos, popups, iframes, cookies, downloads…)
          </a>
        </div>

        <p className="mt-2 text-xs text-content-subtle text-center">
          This page is public — no login required.
        </p>
      </div>
    </div>
  );
}
